import type { ReactNode } from 'react';
import type { Severity } from '../lib/types';

/**
 * Class names here are the PROTOTYPE'S, not invented ones — `.lr .src`,
 * `.lr .lb`, `.stat .v2`, `.due b`, `.p-ok`. They were read out of the
 * extracted stylesheet rather than guessed, because a component that renders
 * plausible-but-wrong class names produces an unstyled screen that looks like
 * a layout bug rather than a naming mismatch.
 */

export type PillKind = 'ok' | 'warn' | 'bad' | 'info' | 'neutral' | 'violet' | 'teal';

export function Pill({ kind, children }: { kind: PillKind; children: ReactNode }) {
  return <span className={`pill p-${kind}`}>{children}</span>;
}

export const SEVERITY_PILL: Record<Severity, PillKind> = {
  critical: 'bad',
  high: 'warn',
  medium: 'warn',
  low: 'neutral',
};

/** Whole days until `date`; negative means overdue. */
export function daysUntil(date: string | null): number | null {
  if (!date) return null;
  const t = new Date(date);
  if (Number.isNaN(t.getTime())) return null;
  const at = new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
  const n = new Date();
  const today = new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime();
  return Math.round((at - today) / 86_400_000);
}

export const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

/**
 * Two-line due badge.
 *
 * The relative line is the point: a bare date makes the reader do the
 * arithmetic, and on a compliance calendar that is exactly where things slip.
 */
export function DueBadge({ date, closedLabel }: { date: string | null; closedLabel?: string }) {
  if (closedLabel) {
    return (
      <span className="due">
        <span style={{ color: '#7b8da4' }}>{closedLabel}</span>
      </span>
    );
  }
  if (!date) return <span className="na">—</span>;
  const n = daysUntil(date);
  let cls = '';
  let rel = '';
  if (n !== null) {
    if (n < 0) {
      cls = 'over';
      rel = `${Math.abs(n)} d overdue`;
    } else if (n === 0) {
      cls = 'soon';
      rel = 'due today';
    } else if (n <= 14) {
      cls = 'soon';
      rel = `in ${n} d`;
    } else {
      rel = `in ${n} d`;
    }
  }
  return (
    <span className="due">
      <b>{fmtDate(date)}</b>
      <span className={cls}>{rel}</span>
    </span>
  );
}

/** Initials circle plus display name. Replace with Entra photos when wired. */
export function Owner({
  refKey,
  analysts,
}: {
  refKey: string | null;
  analysts: Record<string, string>;
}) {
  if (!refKey) return <span className="na">Unassigned</span>;
  // An analyst ref with no directory entry shows the raw ref rather than a
  // blank — an unresolved owner is a data-quality signal, not a nobody.
  const name = analysts[refKey] ?? refKey;
  return (
    <span className="own">
      <span className="ava">{refKey}</span>
      {name}
    </span>
  );
}

/** Lane row — the shared unit of both Posture directions and the drawers. */
export function LaneRow({
  severity,
  glyph,
  title,
  sub,
  right,
  onOpen,
}: {
  severity: Severity;
  glyph: { short: string; color: string };
  title: string;
  sub: string;
  right: ReactNode;
  onOpen?: () => void;
}) {
  return (
    <div className={`lr sev-${severity}`} onClick={onOpen} role={onOpen ? 'button' : undefined}>
      <span className="src" style={{ background: glyph.color }}>
        {glyph.short}
      </span>
      <span className="lb">
        <span className="lt">{title}</span>
        <span className="ls">{sub}</span>
      </span>
      <span className="lm">
        <Pill kind={SEVERITY_PILL[severity]}>{severity}</Pill>
        <span className="age">{right}</span>
      </span>
    </div>
  );
}

export type StatTone = '' | 'r' | 'w' | 'g' | 'v' | 't';

/** Stat tile. A tone letter sets the 3px coloured top border. */
export function Stat({
  tone = '',
  label,
  value,
  hint,
  onClick,
}: {
  tone?: StatTone;
  label: string;
  /** null renders "—". A tile that cannot be computed must never show 0. */
  value: number | string | null;
  hint?: string;
  onClick?: () => void;
}) {
  return (
    <div
      className={`stat${tone ? ` ${tone}` : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      style={onClick ? undefined : { cursor: 'default' }}
    >
      {/* <div>, as the prototype has it: .l / .v2 / .h carry margin-top, which
          an inline <span> ignores, so the label, number and hint ran together
          on one line (found by rendering it, 2026-10-05). */}
      <div className="l">{label}</div>
      <div className="v2">{value === null ? <span className="cw-unread">—</span> : value}</div>
      {hint && <div className="h">{hint}</div>}
    </div>
  );
}

/** Empty-state block for a program table with no rows yet. */
export function EmptyProgram({ what, why }: { what: string; why: string }) {
  return (
    <div className="cw-empty">
      <b>No {what} yet</b>
      <span>{why}</span>
    </div>
  );
}
